import React, { useState, useEffect } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import pestReportService from "../../../api/pestReportService";
import { getTodayUtcMs } from "../../../utils/dateUtils";
import { isGhostOccurrence } from "../utils";

const getColor = (pct) => {
  if (pct >= 80) return { fill: "#19B47A", text: "#0F8A5D" };
  if (pct >= 50) return { fill: "#378ADD", text: "#1D6FD1" };
  if (pct >= 25) return { fill: "#F0A128", text: "#B56A00" };
  return { fill: "#E24B4A", text: "#BE2F2E" };
};

const sortTechs = (list) =>
  [...list].sort((a, b) => {
    const pa = a.uploaded / a.total;
    const pb = b.uploaded / b.total;
    if (Math.abs(pa - pb) > 0.001) return pb - pa;
    if (a.total !== b.total) return a.total - b.total;
    return a.total - a.uploaded - (b.total - b.uploaded);
  });

/**
 * ProgressBars — Barras de progreso del calendario
 *
 * Props:
 *   events         → TODOS los eventos del mes (sin filtro) — para barra izquierda
 *   filteredEvents → eventos filtrados por técnico/zona — para barra derecha cuando hay filtro
 *   currentDate    → fecha activa del calendario
 *   filterType     → "TECHNICIAN" | "ZONE" | ""
 *   filterValue    → nombre del técnico/zona activo, o ""
 */
export const ProgressBars = ({
  events = [],
  filteredEvents = [],
  currentDate = new Date(),
  filterType = "",
  filterValue = "",
}) => {
  const [data, setData] = useState({
    mipCompleted: 0,
    totalMip: 0,
    servicesWithReport: 0,
    totalServicesUntilToday: 0,
    techActive: [],
    techDone: [],
    techActiveTotal: 0,
    techDoneTotal: 0,
    filterLabel: "",
    loading: false,
  });

  useEffect(() => {
    if (!events?.length && !filteredEvents?.length) return;

    const run = async () => {
      setData((p) => ({ ...p, loading: true }));

      try {
        // Año y mes del calendario (usando métodos locales porque currentDate
        // viene de useState(new Date()) que es local, no UTC)
        const year  = currentDate.getFullYear();
        const month = currentDate.getMonth(); // 0-11

        // "Hoy" operativo: el día cambia a las 5:00 AM
        const today = new Date(getTodayUtcMs());

        // ── Helper: deduplicar por _id / id ────────────────────────────────
        const dedupe = (arr) => {
          const seen = new Set();
          return arr.filter((e) => {
            const id = String(e._id ?? e.id);
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
        };

        // ── Helper: eventos del mes actual hasta hoy (inclusive) ────────────
        // isoDate viene del backend como "2025-05-26T00:00:00.000Z"
        // Usamos getUTCDate/Month/FullYear para no sufrir desfase de timezone
        const filterUntilToday = (arr) =>
          arr.filter((e) => {
            if (!e.isoDate) return false;
            const d = new Date(e.isoDate);
            const eventDay = new Date(
              Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0)
            );
            // Debe ser del mes visible en el calendario
            const isCurrentMonth =
              d.getUTCFullYear() === year && d.getUTCMonth() === month;
            return isCurrentMonth && eventDay <= today;
          });

        // ── 1. Eventos globales únicos (denominador barra izquierda) ────────
        const isServiceItem = (e) =>
          (e._isFromServices === true || !e.eventType) && !isGhostOccurrence(e);
        const uniqueAll = dedupe(events).filter(isServiceItem);

        // ── 2. Fuente para barra derecha ────────────────────────────────────
        //    Con filtro activo → solo los eventos filtrados
        //    Sin filtro        → todos los del mes
        const sourceForReports =
          filterType && filterValue
            ? dedupe(filteredEvents).filter(isServiceItem)
            : uniqueAll;

        const untilTodayForReports = filterUntilToday(sourceForReports);

        // ── 3. Fetch de reportes del año (se filtran por evento del mes) ───
        const allReports = await pestReportService.getAllReports({ year });

        const eventIdsInScope = new Set(
          untilTodayForReports.map((e) => String(e._id ?? e.id))
        );

        const reportsThisMonth = allReports.filter((r) => {
          const eventId = r.event
            ? typeof r.event === "string"
              ? r.event
              : String(r.event._id ?? r.event.id ?? "")
            : r.eventId
              ? String(r.eventId)
              : "";
          return eventId && eventIdsInScope.has(eventId);
        });

        // ── 4. Set de IDs de eventos con reporte ────────────────────────────
        //    r.event viene populado del backend como objeto { _id, ... }
        //    Convertimos todo a String para comparación segura
        const reportedIds = new Set(
          reportsThisMonth
            .map((r) => {
              if (r.event) {
                return typeof r.event === "string"
                  ? r.event
                  : String(r.event._id ?? r.event.id ?? "");
              }
              return r.eventId ? String(r.eventId) : null;
            })
            .filter(Boolean)
        );

        // ── 5. Barra izquierda: servicios COMPLETADOS del mes (global) ──────
        const mipCompleted = uniqueAll.filter(
          (e) => e.status === "COMPLETED"
        ).length;

        // ── 6. Barra derecha: eventos con reporte subido ────────────────────
        const withReport = untilTodayForReports.filter((e) =>
          reportedIds.has(String(e._id ?? e.id))
        ).length;

        // ── 7. Desglose por técnico ─────────────────────────────────────────
        const techMap = new Map();

        untilTodayForReports.forEach((e) => {
          const tech = e.technician;
          if (!tech) return;

          const id =
            typeof tech === "string" ? tech : String(tech._id ?? tech.id);

          const name =
            typeof tech === "object"
              ? (tech.name ?? tech.fullName ?? tech.nombre ?? `Técnico ${id.slice(-4)}`)
              : `Técnico ${id.slice(-4)}`;

          if (!techMap.has(id)) {
            techMap.set(id, { id, name, total: 0, uploaded: 0 });
          }

          const entry = techMap.get(id);
          entry.total += 1;
          if (reportedIds.has(String(e._id ?? e.id))) {
            entry.uploaded += 1;
          }
        });

        const allTechs = Array.from(techMap.values());
        const activeSorted = sortTechs(allTechs.filter((t) => t.uploaded < t.total));
        const done = allTechs.filter((t) => t.total > 0 && t.uploaded >= t.total);

        setData({
          mipCompleted,
          totalMip: uniqueAll.length,
          servicesWithReport: withReport,
          totalServicesUntilToday: untilTodayForReports.length,
          techActive: activeSorted.slice(0, 15),
          techActiveTotal: activeSorted.length,
          techDone: done.slice(0, 3),
          techDoneTotal: done.length,
          filterLabel: filterType && filterValue ? filterValue : "",
          loading: false,
        });
      } catch (err) {
        console.error("ProgressBars error:", err);
        setData((p) => ({ ...p, loading: false }));
      }
    };

    run();
  }, [events, filteredEvents, currentDate, filterType, filterValue]);

  const mipPct =
    data.totalMip > 0
      ? Math.round((data.mipCompleted / data.totalMip) * 100)
      : 0;

  const repPct =
    data.totalServicesUntilToday > 0
      ? Math.round((data.servicesWithReport / data.totalServicesUntilToday) * 100)
      : 0;

  const { fill: repFill, text: repText } = getColor(repPct);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: "24px",
        bgcolor: "#fff",
        border: "1px solid rgba(15,23,42,.06)",
        borderRadius: "16px",
        px: 3,
        py: 2,
        boxShadow: "0 1px 2px rgba(0,0,0,.04), 0 4px 16px rgba(0,0,0,.04)",
        opacity: data.loading ? 0.6 : 1,
        overflow: "visible",
        position: "relative",
      }}
    >
      {/* ── Barra izquierda: Servicios completados (siempre global del mes) ── */}
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 500,
              color: "rgba(15,23,42,.6)",
              letterSpacing: ".04em",
              textTransform: "uppercase",
            }}
          >
            Servicios completados
          </Typography>

          <Typography
            sx={{ fontSize: "11px", fontWeight: 500, color: "#378ADD" }}
          >
            {data.mipCompleted}/{data.totalMip} — {mipPct}%
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={mipPct}
          sx={{
            height: 6,
            borderRadius: 999,
            bgcolor: "rgba(15,23,42,.08)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              backgroundColor: "#378ADD",
            },
          }}
        />
      </Box>

      {/* ── Barra derecha: Reportes subidos ── */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "rgba(15,23,42,.6)", letterSpacing: ".04em", textTransform: "uppercase" }}>
            Reportes subidos
          </Typography>
          <Typography sx={{ fontSize: "11px", fontWeight: 500, color: repText }}>
            {data.servicesWithReport}/{data.totalServicesUntilToday} — {repPct}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={repPct}
          sx={{
            height: 6,
            borderRadius: 999,
            bgcolor: "rgba(15,23,42,.08)",
            "& .MuiLinearProgress-bar": { borderRadius: 999, backgroundColor: repFill },
          }}
        />
      </Box>
    </Box>
  );
};